import React from 'react';
import { Link } from 'react-router-dom';
import GameCTA from '../../components/GameCTA';

const ConfusedFeelings: React.FC = () => {
  return (
    <div className="min-h-screen bg-tal-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <Link to="/games" className="text-tal-blue-600 hover:text-tal-blue-700 flex items-center gap-2 font-medium transition-colors">
            ← Back to Games
          </Link>
        </div>

        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <div
            className="bg-gradient-to-r from-tal-blue-700/70 to-tal-slate-600/70 px-8 py-16 text-white text-center bg-cover bg-top bg-blend-overlay"
            style={{ backgroundImage: "url('/games/CF_header.jpg')" }}
          >
            <h1 className="text-5xl font-bold mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8), -2px -2px 4px rgba(0,0,0,0.8), 2px -2px 4px rgba(0,0,0,0.8), -2px 2px 4px rgba(0,0,0,0.8)' }}>Confused Feelings</h1>
            <p className="text-xl opacity-90" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8), -1px -1px 3px rgba(0,0,0,0.8), 1px -1px 3px rgba(0,0,0,0.8), -1px 1px 3px rgba(0,0,0,0.8)' }}>A Journey Through the Heart</p>
            <div className="mt-6">
              <span className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">
                Coming Soon
              </span>
            </div>
          </div>

          <div className="px-8 py-10 space-y-8">
            {/* Overview */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-blue-200">
                Overview
              </h2>
              <p className="text-tal-slate-700 leading-relaxed text-lg">
                Confused Feelings is an intimate narrative experience that explores the beautiful complexity of human
                emotions. Through meaningful choices and authentic relationships, discover what it means to truly
                understand yourself and others in a world where feelings are never simple.
              </p>
            </section>

            {/* Story */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-blue-200">
                Story
              </h2>
              <p className="text-tal-slate-700 leading-relaxed mb-4">
                Step into the life of a college student navigating the turbulent waters of young adulthood, forced to take 
                a summer job at a summer get-away-home.  Finds herself entangled in friendships, relationship, rivialry, 
                and the constant pressure to have it all figured out, you're dealing with something
                even more complicated—understanding your own heart.
              </p>
              <p className="text-tal-slate-700 leading-relaxed">
                When unexpected feelings surface for someone you never anticipated, everything you thought you knew
                about yourself comes into question. Through a series of life-changing encounters and heartfelt
                conversations, you'll explore themes of identity, connection, and the courage it takes to be vulnerable.
              </p>
            </section>

            {/* Key Features */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-blue-200">
                Key Features
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-tal-blue-50 p-6 rounded-lg border-l-4 border-tal-blue-500">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">💝 Authentic Relationships</h3>
                  <p className="text-tal-slate-700">
                    Build meaningful connections with a diverse cast of characters. Every relationship evolves based
                    on your choices, creating unique and personal story arcs.
                  </p>
                </div>
                <div className="bg-tal-blue-50 p-6 rounded-lg border-l-4 border-tal-blue-500">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">🎭 Multiple Perspectives</h3>
                  <p className="text-tal-slate-700">
                    Experience key moments from different points of view. Understand that there's always more to
                    the story than what first meets the eye.
                  </p>
                </div>
                
                <div className="bg-tal-blue-50 p-6 rounded-lg border-l-4 border-tal-blue-500">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">✨ Meaningful Choices</h3>
                  <p className="text-tal-slate-700">
                    Every decision matters. Shape your relationships, your path, and your understanding of what
                    truly matters in life.
                  </p>
                </div>
              </div>
            </section>

            {/* Themes */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-blue-200">
                Themes & Topics
              </h2>
              <div className="space-y-4">
                <div className="bg-tal-slate-50 p-5 rounded-lg">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">Self-Discovery</h3>
                  <p className="text-tal-slate-700 leading-relaxed">
                    The journey of understanding who you are, what you want, and having the courage to be true to
                    yourself even when it's scary or confusing.
                  </p>
                </div>
                <div className="bg-tal-slate-50 p-5 rounded-lg">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">Emotional Intelligence</h3>
                  <p className="text-tal-slate-700 leading-relaxed">
                    Learn to navigate complex emotions, communicate effectively, and understand the nuances of human
                    connection in all its forms.
                  </p>
                </div>
                <div className="bg-tal-slate-50 p-5 rounded-lg">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">Love & Connection</h3>
                  <p className="text-tal-slate-700 leading-relaxed">
                    Explore different forms of love—romantic, platonic, familial—and discover that caring deeply for
                    someone can take many beautiful shapes.
                  </p>
                </div>
                <div className="bg-tal-slate-50 p-5 rounded-lg">
                  <h3 className="text-xl font-semibold text-tal-slate-800 mb-2">Growing Up</h3>
                  <p className="text-tal-slate-700 leading-relaxed">
                    The messy, wonderful, terrifying process of becoming who you're meant to be while learning to
                    let go of who you thought you should be.
                  </p>
                </div>
              </div>
            </section>

            {/* Characters Preview */}
            <section>
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-4 pb-2 border-b-2 border-tal-blue-200">
                Meet Some Characters
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4">
                  <div className="text-5xl mb-3">👤</div>
                  <h3 className="text-lg font-semibold text-tal-slate-800 mb-2">Harper Bistro</h3>
                  <p className="text-sm text-tal-slate-600">
                    You—trying to figure out life, love, and everything in between.
                  </p>
                </div>
                <div className="text-center p-4">
                  <div className="text-5xl mb-3">🎨</div>
                  <h3 className="text-lg font-semibold text-tal-slate-800 mb-2">The Artist</h3>
                  <p className="text-sm text-tal-slate-600">
                    A free spirit who sees beauty, and lives life care-free and causes chaos where ever he goes.
                  </p>
                </div>
                <div className="text-center p-4">
                  <div className="text-5xl mb-3">📚</div>
                  <h3 className="text-lg font-semibold text-tal-slate-800 mb-2">The Entrepreneur</h3>
                  <p className="text-sm text-tal-slate-600">
                    Brilliant and kind, but cold and afraid to let anyone see his vulnerable side.
                  </p>
                </div>
              </div>
            </section>

            {/* Stay Updated */}
            <GameCTA
              title="Be Part of the Journey"
              description="Sign up for updates about Confused Feelings and be among the first to experience this heartfelt story when it launches."
              gradientFrom="from-tal-blue-700"
              gradientTo="to-tal-slate-600"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfusedFeelings;
